"use client";

import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";
import { SafeIcon } from "./SafeIcon";
import { CardDescriptionHtml } from "@/components/visual-cms/CardDescriptionHtml";

interface CardGridSectionProps {
  section: VisualCmsSection;
}

function readString(c: Record<string, unknown>, key: string): string {
  const v = c[key];
  return typeof v === "string" ? v : "";
}

function readColumns(c: Record<string, unknown>): 2 | 3 | 4 {
  const v = c.columns;
  if (typeof v === "number" && [2, 3, 4].includes(v)) return v as 2 | 3 | 4;
  return 3;
}

const gridClassByCols: Record<2 | 3 | 4, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
};

export function CardGridSection({ section }: CardGridSectionProps) {
  const content = (section.content as Record<string, unknown>) ?? {};
  const title = readString(content, "title");
  const subtitle = readString(content, "subtitle");
  const cols = readColumns(content);
  const items = (section.cards ?? []).filter((c) => c.is_active !== false);
  const shell = sectionShellStyle(section);

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        {(title || subtitle) && (
          <div className="max-w-3xl mx-auto text-center mb-10">
            {title && <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">{title}</h2>}
            {subtitle && (
              <p className="mt-3 text-base sm:text-lg text-current/70">{subtitle}</p>
            )}
          </div>
        )}

        {items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center text-sm text-current/60">
            No cards yet — add some from the admin editor.
          </div>
        ) : (
          <div className={`grid grid-cols-1 ${gridClassByCols[cols]} gap-5 sm:gap-6`}>
            {items.map((card) => (
              <article
                key={card.id}
                className="rounded-xl border border-current/10 bg-current/[0.02] p-6 flex flex-col"
              >
                {card.image ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={card.image}
                    alt={card.title || ""}
                    className="w-full h-40 object-cover rounded-md mb-4"
                  />
                ) : card.icon ? (
                  <div className="h-10 w-10 rounded-lg bg-green-100 text-green-700 flex items-center justify-center mb-4">
                    <SafeIcon name={card.icon} className="h-5 w-5" />
                  </div>
                ) : null}

                {card.title && (
                  <h3 className="text-lg font-semibold leading-tight">{card.title}</h3>
                )}
                {card.description && (
                  <CardDescriptionHtml
                    as="p"
                    text={card.description}
                    className="mt-2 text-sm text-current/75 leading-relaxed"
                    preserveLineBreaks
                  />
                )}
                {card.button_text && (
                  <a
                    href={card.button_link || "#"}
                    className="mt-4 inline-flex items-center text-sm font-semibold text-green-700 hover:underline"
                  >
                    {card.button_text} →
                  </a>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
